import { THEME_FONT_STACKS } from '../theme.ts';
import type { FinishOptions } from './finishes.ts';

/**
 * O acabamento em variáveis de CSS (ADR 0133, parte 1). Cada ajuste vira uma ou mais variáveis
 * que as peças de `apps/web/src/components/` leem (`styles/finish.css`). Variável, e não marca no
 * `<html>`, porque a mais perto vale: a prévia do estúdio põe as dela por cima das do sistema sem
 * regra especial. As cores continuam no tema; aqui só forma, letra e jeito de cada peça.
 */

/** A letra da marca, quando o acabamento não troca (`base`). */
const BRAND_FONT = "'Geist Sans', ui-sans-serif, system-ui, sans-serif";
const fontStack = (f: string): string =>
  f === 'base'
    ? BRAND_FONT
    : (THEME_FONT_STACKS[f as keyof typeof THEME_FONT_STACKS] ?? BRAND_FONT);

export function finishCssVars(f: FinishOptions): Record<string, string> {
  const v: Record<string, string> = {};
  // Letra.
  v['--font-head'] = f.headFont === 'same' ? 'var(--font-sans)' : fontStack(f.headFont);
  v['--text-scale'] = String(parseInt(f.textSize, 10) / 14);
  v['--head-weight'] = f.headWeight;
  v['--head-tracking'] = f.tracking === 'tight' ? '-0.015em' : '0';
  v['--cap-transform'] = f.labelCase === 'upper' ? 'uppercase' : 'none';
  v['--cap-spacing'] = f.labelCase === 'upper' ? '0.05em' : '0';
  v['--num-font'] = f.numFont === 'mono' ? 'var(--font-mono)' : 'inherit';
  // Forma.
  v['--btn-radius'] =
    f.btnShape === 'pill' ? '999px' : f.btnShape === 'square' ? '1px' : 'var(--radius-md)';
  v['--avatar-radius'] = f.avatar === 'circle' ? '999px' : 'var(--radius-md)';
  const shadow = f.panel === 'shadow' || f.panel === 'both';
  const border = f.panel === 'border' || f.panel === 'both';
  // As mesmas sombras do tema (themeShapeVars): forte com sombra, leve com só a borda.
  v['--panel-shadow'] = shadow
    ? '0 1px 3px rgb(0 0 0 / 0.08), 0 4px 14px rgb(0 0 0 / 0.06)'
    : border
      ? '0 1px 2px rgb(23 32 43 / 0.06), 0 1px 3px rgb(23 32 43 / 0.04)'
      : 'none';
  v['--panel-border'] = border ? 'var(--rule)' : 'transparent';
  v['--panel-bg'] = f.panel === 'flat' ? 'var(--paper)' : 'var(--sheet)';
  // Item marcado no menu: fundo discreto, barrinha na cor de destaque ou a própria cor.
  v['--nav-on-bg'] = f.activeMark === 'pill' ? 'var(--sidebar-active)' : 'var(--sidebar-hover)';
  v['--nav-on-fg'] = f.activeMark === 'pill' ? 'var(--sidebar-active-text)' : 'var(--sidebar-text)';
  v['--nav-on-bar'] = f.activeMark === 'bar' ? 'var(--sidebar-active)' : 'transparent';
  // Ícones.
  v['--icon-stroke'] = f.iconStroke;
  v['--icon-size'] = `${parseInt(f.iconSize, 10)}px`;
  v['--icon-fill'] =
    f.iconStyle === 'solid'
      ? 'currentColor'
      : f.iconStyle === 'duotone'
        ? 'color-mix(in srgb, currentColor 18%, transparent)'
        : 'none';
  // Ícone cheio: o traço na cor do fundo recorta o desenho por dentro (como no protótipo).
  v['--icon-knock'] = f.iconStyle === 'solid' ? 'var(--sidebar)' : 'currentColor';
  v['--icon-support'] = f.iconTint === 'accent' ? 'var(--brand-text)' : 'var(--muted)';
  // Botões.
  const pri = (
    {
      solid: ['var(--accent)', 'var(--on-accent)', 'var(--accent)'],
      soft: ['var(--brand-soft)', 'var(--brand-text)', 'transparent'],
      outline: ['transparent', 'var(--brand-text)', 'var(--accent)'],
    } as const
  )[f.btnPrimary];
  [v['--btn-pri-bg'], v['--btn-pri-fg'], v['--btn-pri-border']] = pri;
  const sec = (
    {
      tonal: ['var(--tonal)', 'transparent'],
      outline: ['var(--sheet)', 'var(--control-border)'],
      ghost: ['transparent', 'transparent'],
    } as const
  )[f.btnSecondary];
  [v['--button-secondary-bg'], v['--button-secondary-border']] = sec;
  v['--btn-dir'] = f.btnIcon === 'right' ? 'row-reverse' : 'row';
  v['--btn-icon-display'] = f.btnIcon === 'none' ? 'none' : 'inline-block';
  // Campos.
  v['--input-bg'] =
    f.input === 'filled'
      ? 'color-mix(in srgb, var(--ink) 8%, var(--paper))'
      : f.input === 'underline'
        ? 'transparent'
        : 'var(--sheet)';
  v['--input-border'] = f.input === 'filled' ? 'transparent' : 'var(--control-border)';
  v['--input-border-width'] = f.input === 'underline' ? '0 0 1px 0' : '1px';
  v['--input-radius'] = f.input === 'underline' ? '0' : 'var(--radius-md)';
  v['--field-display'] = f.labelPos === 'side' ? 'grid' : 'flex';
  // Etiquetas: quanto da cor vai no fundo, a borda, o ponto e a cor do texto.
  const badge = (
    {
      soft: ['14%', '0', 'none', 'var(--c)', '0.5rem'],
      solid: ['100%', '0', 'none', 'var(--sheet)', '0.5rem'],
      outline: ['0%', '1px', 'inline-block', 'var(--c)', '0.5rem'],
      dot: ['0%', '0', 'inline-block', 'var(--ink)', '0'],
    } as const
  )[f.badge];
  [
    v['--badge-fill'],
    v['--badge-border-width'],
    v['--badge-dot'],
    v['--badge-fg'],
    v['--badge-pad-x'],
  ] = badge;
  // Abas.
  const tabs = (
    {
      underline: ['transparent', '0', '0', 'transparent', 'none', '2px', '1px'],
      pill: ['transparent', '0', 'var(--radius-md)', 'var(--brand-soft)', 'none', '0', '0'],
      segmented: [
        'var(--tonal)',
        '3px',
        'var(--radius-md)',
        'var(--sheet)',
        '0 1px 2px rgb(0 0 0 / 0.12)',
        '0',
        '0',
      ],
    } as const
  )[f.tabs];
  [
    v['--tabs-bg'],
    v['--tabs-pad'],
    v['--tab-radius'],
    v['--tab-on-bg'],
    v['--tab-on-shadow'],
    v['--tab-line'],
    v['--tabs-rule'],
  ] = tabs;
  // Avisos.
  const alert = (
    {
      soft: ['8%', '1px', '1px', 'var(--c)'],
      bar: ['0%', '1px', '3px', 'var(--c)'],
      solid: ['100%', '0', '0', 'var(--sheet)'],
    } as const
  )[f.alert];
  [v['--alert-fill'], v['--alert-border-width'], v['--alert-bar'], v['--alert-fg']] = alert;
  // Tabelas.
  v['--row-rule'] = f.tableRows === 'lines' || f.tableBorder === 'grid' ? '1px' : '0';
  v['--row-zebra'] =
    f.tableRows === 'zebra' ? 'color-mix(in srgb, var(--ink) 3%, var(--sheet))' : 'transparent';
  v['--cell-rule'] = f.tableBorder === 'grid' ? '1px' : '0';
  v['--row-actions-idle'] = f.rowActions === 'hover' ? '0' : '1';
  return v;
}

/** As variáveis em texto de CSS, para uma regra (`:root{…}` ou a da prévia). */
export function finishCss(f: FinishOptions, selector = ':root'): string {
  const decl = Object.entries(finishCssVars(f))
    .map(([k, val]) => `${k}:${val};`)
    .join('');
  return `${selector}{${decl}}`;
}
